import { create } from 'zustand'
import { mapApi } from '../api/map'

export interface PlaceCategoryInfo {
  code: string
  name: string
  icon_path: string | null
  marker_pin_path: string | null
  marker_pin_bookmarked_path: string | null
  category_icon_brown_path: string | null
}

interface CategoryStore {
  categories: PlaceCategoryInfo[] // 카테고리 목록
  categoryMap: Record<string, PlaceCategoryInfo> // code -> PlaceCategoryInfo
  isLoading: boolean
  isInitialized: boolean
  loadCategories: () => Promise<void>
  // 카테고리 정보 조회
  getCategory: (code: string) => PlaceCategoryInfo | null
  getCategoryName: (code: string) => string
  getAllCategories: () => PlaceCategoryInfo[]
  getCategoryCodes: () => string[]
  // 아이콘 조회
  getCategoryIconBrown: (code: string) => string | null
  getMarkerPin: (code: string, isBookmarked?: boolean) => string | null
  getCategoryIcon: (code: string) => string | null
}

/**
 * 화면에 내놓는 차례 — 레스토랑, 카페, 술집.
 *
 * 왜 여기서 맞추나
 * ---------------
 * 서버의 목록 쿼리(`PlaceCategory::all()`, MapController.getPlaceCategories)에는
 * **ORDER BY 가 없다.** 그래서 내려오는 차례가 DB 행 번호를 그대로 따라간다.
 * 운영에서는 레스토랑 행의 번호가 맨 뒤라 **카페·술집·레스토랑** 순으로 온다
 * (2026-10-17 확인). 마이그레이션은 전부 레스토랑을 맨 먼저 넣고
 * `App\Enums\PlaceCategory` 도 restaurant·cafe·bar 차례이므로, 지금 순서는
 * 의도된 것이 아니라 행 번호가 바뀐 흔적이다.
 *
 * 차례는 서버 사정이 아니라 정해진 것이다. 받은 뒤 여기서 한 번 맞춘다 —
 * 목록을 읽는 화면(랭킹 탭·기록 보기·플레이트·기록 쓰기)이 전부 이 store 를
 * 보므로 여기 한 곳이면 전부 맞는다.
 *
 * 모르는 코드는 **뒤로** 보낸다. 나중에 카테고리가 늘어도 맨 앞으로 튀어나오지
 * 않는다(sort 는 안정 정렬이라 그들끼리는 서버가 준 차례를 지킨다).
 */
const CATEGORY_ORDER = ['restaurant', 'cafe', 'bar']

export const categoryRank = (code: string): number => {
  const i = CATEGORY_ORDER.indexOf(code)
  return i === -1 ? CATEGORY_ORDER.length : i
}

export const useCategoryStore = create<CategoryStore>((set, get) => ({
  categories: [],
  categoryMap: {},
  isLoading: false,
  isInitialized: false,

  loadCategories: async () => {
    const { isInitialized, isLoading } = get()

    // 이미 초기화되었거나 로딩 중이면 중복 호출 방지
    if (isInitialized || isLoading) {
      return
    }

    set({ isLoading: true })

    try {
      const response = await mapApi.getPlaceCategories()
      const categories: PlaceCategoryInfo[] = response.categories
        .map((cat) => ({
          code: cat.code,
          name: cat.name,
          icon_path: cat.icon_path,
          marker_pin_path: cat.marker_pin_path,
          marker_pin_bookmarked_path: cat.marker_pin_bookmarked_path,
          category_icon_brown_path: cat.category_icon_brown_path,
        }))
        .sort((a, b) => categoryRank(a.code) - categoryRank(b.code))

      const categoryMap: Record<string, PlaceCategoryInfo> = {}
      categories.forEach((category) => {
        categoryMap[category.code] = category
      })

      set({
        categories,
        categoryMap,
        isInitialized: true,
      })
    } catch (error) {
      console.error('카테고리 로드 실패:', error)
    } finally {
      set({ isLoading: false })
    }
  },

  getCategory: (code: string) => {
    return get().categoryMap[code] || null
  },

  getCategoryName: (code: string) => {
    const category = get().categoryMap[code]
    return category?.name || code
  },

  getAllCategories: () => {
    return get().categories
  },

  getCategoryCodes: () => {
    return get().categories.map((cat) => cat.code)
  },

  getCategoryIconBrown: (code: string) => {
    const category = get().categoryMap[code]
    return category?.category_icon_brown_path || null
  },

  getMarkerPin: (code: string, isBookmarked = false) => {
    const category = get().categoryMap[code]
    if (isBookmarked) {
      return category?.marker_pin_bookmarked_path || null
    }
    return category?.marker_pin_path || null
  },

  getCategoryIcon: (code: string) => {
    const category = get().categoryMap[code]
    return category?.icon_path || null
  },
}))
